import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import {
  HiCollection,
  HiCash,
  HiArrowLeft,
  HiCreditCard,
  HiCalendar,
  HiChartBar,
  HiTrendingUp,
  HiX,
  HiDownload,
  HiUpload,
  HiPlus,
  HiTag
} from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import FormInput from '../../../components/ui/Form/FormInput';
import FormToggle from '../../../components/ui/Form/FormToggle';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import BulkImportModal from '../../../components/ui/BulkImportModal';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow, SortDirection } from '../../../components/ui/Table';
import { formatDateOnly, formatTimeOnly } from '../../../utils/dateFormatters';
import { extractEventIdFromAdminUrl } from '../../../utils/urlSlug';
import styles from './EventParticipantsPage.module.css';

import { UserProfileLink } from '../../../components/ui/UserDisplay';
import { OrganizerPanelOutletContext, ExtendedSoldTicket } from '../../../types/organizerPanel.types';
import { useEventDetails } from '../../../hooks/useEventDetails';
import { useSegmentations } from '../../../hooks/useSegmentations';
import UserTagsDisplay from '../../../components/organizerPanel/UserTagsDisplay/UserTagsDisplay';
import TagDropdown from '../../../components/organizerPanel/TagDropdown/TagDropdown';

const EventParticipantsPage: React.FC = () => {
  const { event: basicEvent, channel } = useOutletContext<OrganizerPanelOutletContext>();
  const location = useLocation();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  const [participants, setParticipants] = useState<ExtendedSoldTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<ExtendedSoldTicket | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedStats, setSelectedStats] = useState<string[]>([
    'totalSold',
    'totalRevenue',
    'participationRate',
    'dailySales'
  ]);

  // Segmentation state
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [activeTagDropdown, setActiveTagDropdown] = useState<string | null>(null);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [pendingBulkTags, setPendingBulkTags] = useState<Record<string, 'add' | 'remove'>>({});
  const tagDropdownAnchorRef = useRef<HTMLSpanElement>(null);

  // Extract eventID from URL as fallback when basicEvent isn't available yet
  const eventID = useMemo(() => {
    if (basicEvent?.eventID) return basicEvent.eventID;
    return extractEventIdFromAdminUrl(location.pathname) || '';
  }, [basicEvent?.eventID, location.pathname]);

  // Fetch event details to get ticket prices
  const { event: eventDetails } = useEventDetails({
    token: token || null,
    eventID,
  });

  // Segmentation hook
  const {
    segments,
    addSegment,
    addTagToUsers,
    removeTagFromUsers,
  } = useSegmentations({
    token,
    channelID: channel?.channelID,
  });

  // Create ticket price map for O(1) lookup
  const ticketPriceMap = useMemo(() => {
    const map = new Map<string, number>();
    (eventDetails?.tickets || []).forEach(ticket => {
      if (ticket.ticketID) {
        map.set(String(ticket.ticketID), Number(ticket.price) || 0);
      }
    });
    return map;
  }, [eventDetails?.tickets]);

  // Get ticket currency from event tickets
  const getTicketCurrency = (ticketID: string): string => {
    const ticket = eventDetails?.tickets?.find(t => String(t.ticketID) === ticketID);
    return ticket?.currency || "TRY";
  };

  // Get ticket price following the guide's priority logic
  const getTicketPrice = (participant: ExtendedSoldTicket): string => {
    const ticketID = String(participant.ticketID || "");
    const individualPrice = ticketPriceMap.get(ticketID);
    
    // Priority: ticketPriceMap > paid > totalPaid > 0
    const amount = individualPrice !== undefined 
      ? individualPrice 
      : (toNumber(participant.paid ?? participant.totalPaid ?? participant.price ?? 0));
    
    // Return "Free" for zero prices, otherwise formatted price with currency
    if (amount === 0) {
      return getTranslation('organizerPanel.free') || 'Free';
    }
    
    const currency = getTicketCurrency(ticketID);
    return `${Number(amount).toLocaleString("en-US")} ${currency}`;
  };

  // Fetch participants function (reusable)
  const fetchParticipants = async () => {
    if (!token || !basicEvent?.eventID) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await Services.SocialHiveGetEventSoldTickets(token, basicEvent.eventID);
      if (response.data.status && response.data.result) {
        setParticipants(response.data.result as ExtendedSoldTicket[]);
      } else {
        setParticipants([]);
      }
    } catch (error: unknown) {
      toastError(getTranslation('organizerPanel.fetchParticipantsError') || 'Katılımcılar yüklenemedi');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch participants when event changes
  useEffect(() => {
    fetchParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, basicEvent?.eventID]);

  // Get participant status
  const getParticipantStatus = (participant: ExtendedSoldTicket): string => {
    // Check usedAt first (primary field from SoldTicket interface)
    const isUsed = participant.usedAt !== null && participant.usedAt !== undefined && participant.usedAt > 0;
    // Also check isUsed field if present (from ExtendedSoldTicket)
    const isUsedFlag = participant.isUsed === "1" || participant.isUsed === true;
    // Check status field if present
    const isCancelled = participant.status === "0";
    // Check refund field if present
    const isRefund = participant.isRefund === "1" || participant.isRefund === true;

    if (isUsed || isUsedFlag) {
      return 'used';
    }
    if (isCancelled) {
      return 'cancelled';
    }
    if (isRefund) {
      return 'refunded';
    }
    return 'notUsed';
  };

  // Check if participant can be checked in
  const canCheckIn = (participant: ExtendedSoldTicket): boolean => {
    const status = getParticipantStatus(participant);
    return status === 'notUsed';
  };

  // Handle check-in toggle click
  const handleCheckInToggle = (participant: ExtendedSoldTicket) => {
    const status = getParticipantStatus(participant);
    // Only allow check-in for unused tickets (not cancelled/refunded)
    if (status === 'notUsed') {
      setSelectedParticipant(participant);
      setShowCheckInModal(true);
    }
  };

  // Handle check-in confirmation
  const handleConfirmCheckIn = async () => {
    if (!token || !selectedParticipant?.ticketNo) return;

    setCheckingIn(selectedParticipant.ticketNo);
    try {
      await Services.SocialHiveUseTicket(token, selectedParticipant.ticketNo);
      toastSuccess(getTranslation('organizerPanel.checkInSuccess') || 'Check-in başarılı');
      
      // Update local state to check in
      setParticipants(prev => prev.map(p => 
        p.ticketNo === selectedParticipant.ticketNo
          ? { ...p, isUsed: "1" as const, usedAt: Math.floor(Date.now() / 1000), usedDateTime: Math.floor(Date.now() / 1000) }
          : p
      ));
      
      // Close modal
      setShowCheckInModal(false);
      setSelectedParticipant(null);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.checkInError') || 'Check-in yapılamadı';
      toastError(errorMessage);
    } finally {
      setCheckingIn(null);
    }
  };

  // Segmentation handlers
  const getParticipantTags = (participant: ExtendedSoldTicket): string[] => {
    if (!participant.tags || !channel?.channelID) return [];
    return participant.tags[channel.channelID] || [];
  };

  const getParticipantUserId = (participant: ExtendedSoldTicket): string => {
    return participant.userID || (participant as any).id || '';
  };

  const handleSelectParticipant = (ticketNo: string) => {
    setSelectedParticipants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketNo)) {
        newSet.delete(ticketNo);
      } else {
        newSet.add(ticketNo);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedParticipants.size === filteredAndSortedParticipants.length) {
      setSelectedParticipants(new Set());
    } else {
      setSelectedParticipants(new Set(filteredAndSortedParticipants.map(p => p.ticketNo)));
    }
  };

  const handleCreateAndAssignTag = async (tag: string, participant: ExtendedSoldTicket) => {
    const userId = getParticipantUserId(participant);
    if (!userId) return;

    const created = await addSegment(tag);
    if (!created) return;

    const assigned = await addTagToUsers(tag, [userId]);
    if (assigned) {
      setParticipants(prev => prev.map(p => {
        if (p.ticketNo !== participant.ticketNo) return p;
        const currentTags = getParticipantTags(p);
        return {
          ...p,
          tags: {
            ...p.tags,
            [channel?.channelID || '']: [...currentTags, tag],
          },
        };
      }));
      toastSuccess(getTranslation('organizerPanel.tagAdded') || 'Tag added');
    }
  };

  const handleTagToggle = async (tag: string, isCurrentlyAssigned: boolean, participant: ExtendedSoldTicket) => {
    const userId = getParticipantUserId(participant);
    if (!userId) return;

    const success = isCurrentlyAssigned
      ? await removeTagFromUsers(tag, [userId])
      : await addTagToUsers(tag, [userId]);

    if (success) {
      // Update local state optimistically
      setParticipants(prev => prev.map(p => {
        if (p.ticketNo !== participant.ticketNo) return p;
        const currentTags = getParticipantTags(p);
        const newTags = isCurrentlyAssigned
          ? currentTags.filter(t => t !== tag)
          : [...currentTags, tag];
        return {
          ...p,
          tags: {
            ...p.tags,
            [channel?.channelID || '']: newTags,
          },
        };
      }));
      toastSuccess(
        isCurrentlyAssigned
          ? (getTranslation('organizerPanel.tagRemoved') || 'Tag removed')
          : (getTranslation('organizerPanel.tagAdded') || 'Tag added')
      );
    } else {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to update tag');
    }
  };

  const handleBulkAddTag = (tag: string) => {
    const pending = pendingBulkTags[tag];
    if (pending === 'add') {
      // Undo add
      setPendingBulkTags(prev => {
        const next = { ...prev };
        delete next[tag];
        return next;
      });
    } else {
      setPendingBulkTags(prev => ({ ...prev, [tag]: 'add' }));
    }
  };

  const handleBulkRemoveTag = (tag: string) => {
    const pending = pendingBulkTags[tag];
    if (pending === 'remove') {
      // Undo remove
      setPendingBulkTags(prev => {
        const next = { ...prev };
        delete next[tag];
        return next;
      });
    } else {
      setPendingBulkTags(prev => ({ ...prev, [tag]: 'remove' }));
    }
  };

  const handleSaveBulkTags = async () => {
    const userIds = filteredAndSortedParticipants
      .filter(p => selectedParticipants.has(p.ticketNo))
      .map(p => getParticipantUserId(p))
      .filter(Boolean);

    if (userIds.length === 0) return;

    const entries = Object.entries(pendingBulkTags);
    if (entries.length === 0) {
      setShowBulkTagModal(false);
      return;
    }

    let allSuccess = true;
    for (const [tag, action] of entries) {
      const success = action === 'add'
        ? await addTagToUsers(tag, userIds)
        : await removeTagFromUsers(tag, userIds);

      if (success) {
        setParticipants(prev => prev.map(p => {
          if (!selectedParticipants.has(p.ticketNo)) return p;
          const currentTags = getParticipantTags(p);

          if (action === 'remove') {
            return {
              ...p,
              tags: {
                ...p.tags,
                [channel?.channelID || '']: currentTags.filter(t => t !== tag),
              },
            };
          } else {
            if (currentTags.includes(tag)) return p;
            return {
              ...p,
              tags: {
                ...p.tags,
                [channel?.channelID || '']: [...currentTags, tag],
              },
            };
          }
        }));
      } else {
        allSuccess = false;
      }
    }

    if (allSuccess) {
      toastSuccess(getTranslation('organizerPanel.tagsUpdated') || 'Tags updated successfully');
    } else {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to update tags');
    }

    setPendingBulkTags({});
    setShowBulkTagModal(false);
  };

  const handleCancelBulkTags = () => {
    setPendingBulkTags({});
    setShowBulkTagModal(false);
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    if (!token || participants.length === 0) {
      toastError(getTranslation('organizerPanel.noParticipantsToExport') || 'Dışa aktarılacak katılımcı yok');
      return;
    }

    setExporting(true);
    try {
      const content = participants.map((p, index) => {
        const status = getParticipantStatus(p);
        let statusText = '';
        if (status === 'used') {
          statusText = getTranslation('organizerPanel.statusUsed') || 'Kullanıldı';
        } else if (status === 'cancelled') {
          statusText = getTranslation('organizerPanel.statusCancelled') || 'İptal Edildi';
        } else if (status === 'refunded') {
          statusText = getTranslation('organizerPanel.statusRefunded') || 'İade Edildi';
        } else {
          statusText = getTranslation('organizerPanel.statusNotUsed') || 'Kullanılmadı';
        }

        const paymentDate = p.paymentDateTime || p.createdAt;
        const dateStr = paymentDate ? formatDateOnly(paymentDate) : '';
        const timeStr = paymentDate ? formatTimeOnly(paymentDate) : '';
        const dateTimeStr = dateStr && timeStr ? `${dateStr} ${timeStr}` : '';

        return {
          id: index + 1,
          ticketNo: p.ticketNo,
          name: `${p.name} ${p.lastname}`,
          email: p.email,
          phone: p.phone,
          type: p.title || p.type || getTranslation('organizerPanel.unknownTicket') || 'Bilinmeyen',
          amount: String(p.price || p.paid || p.totalPaid || '0'),
          status: statusText,
          date: dateTimeStr,
        };
      });

      const header = {
        id: "number" as const,
        ticketNo: "string" as const,
        name: "string" as const,
        email: "string" as const,
        phone: "string" as const,
        type: "string" as const,
        amount: "string" as const,
        status: "string" as const,
        date: "string" as const,
      };

      const sheetTitle = getTranslation('organizerPanel.participants') || 'Katılımcılar';

      const response = await Services.ExportToExcel(token, content, header, sheetTitle);
      
      if (response.data.status && response.data.result) {
        const fileUrl = typeof response.data.result === 'string' 
          ? response.data.result 
          : (response.data.result as { fileUrl?: string })?.fileUrl;
        
        if (fileUrl && typeof fileUrl === 'string') {
          // Open download link
          window.open(fileUrl, '_blank');
          toastSuccess(getTranslation('organizerPanel.exportSuccess') || 'Excel dosyası oluşturuldu');
        } else {
          toastError(getTranslation('organizerPanel.exportError') || 'Excel dosyası oluşturulamadı');
        }
      } else {
        toastError(getTranslation('organizerPanel.exportError') || 'Excel dosyası oluşturulamadı');
      }
    } catch (error: unknown) {
      toastError(getTranslation('organizerPanel.exportError') || 'Excel dosyası oluşturulamadı');
    } finally {
      setExporting(false);
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'used':
        return styles.statusBadgeUsed;
      case 'cancelled':
        return styles.statusBadgeCancelled;
      case 'refunded':
        return styles.statusBadgeRefunded;
      default:
        return styles.statusBadgeNotUsed;
    }
  };

  // Get status text
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'used':
        return getTranslation('organizerPanel.statusUsed') || 'Kullanıldı';
      case 'cancelled':
        return getTranslation('organizerPanel.statusCancelled') || 'İptal Edildi';
      case 'refunded':
        return getTranslation('organizerPanel.statusRefunded') || 'İade Edildi';
      default:
        return getTranslation('organizerPanel.statusNotUsed') || 'Kullanılmadı';
    }
  };

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Helper function to convert value to number
  const toNumber = (v: string | number | null | undefined): number => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === "string" ? v.replace(",", ".") : v;
    const parsed = parseFloat(String(n));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Helper function to format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Helper function to check if two timestamps are on the same day (Istanbul timezone)
  const sameDayTR = (unixA: number, unixB: number): boolean => {
    const a = new Date(unixA * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
    const b = new Date(unixB * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
    return a === b;
  };

  // Calculate metrics from participants
  const metrics = useMemo(() => {
    if (!participants || participants.length === 0) {
      return {
        totalSold: 0,
        totalRevenue: 0,
        refundedCount: 0,
        refundedRevenue: 0,
        netBalance: 0,
        usedOnEventDay: 0,
        totalUsed: 0,
        participationRate: 0,
        soldToday: 0,
        soldYesterday: 0,
        dailySalesChange: 0,
        cancelledCount: 0,
      };
    }

    // Filter active tickets (not cancelled and not refunded)
    const activeTickets = participants.filter(
      (t) => t.status !== "0" && t.isRefund !== "1" && t.isRefund !== true
    );

    // Total Sold
    const totalSold = activeTickets.length;

    // Total Revenue (excluding refunded)
    const totalRevenue = participants.reduce((sum, t) => {
      if (t.isRefund === "1" || t.isRefund === true) return sum;
      const amount = toNumber(t.totalPaid ?? t.paid ?? t.price ?? 0);
      return sum + amount;
    }, 0);

    // Refunds
    const refundedTickets = participants.filter(
      (t) => t.isRefund === "1" || t.isRefund === true
    );
    const refundedCount = refundedTickets.length;
    const refundedRevenue = refundedTickets.reduce((sum, t) => {
      const amount = toNumber(t.totalPaid ?? t.paid ?? t.price ?? 0);
      return sum + amount;
    }, 0);

    // Net Balance
    const netBalance = totalRevenue - refundedRevenue;

    // Used on Event Day
    let usedOnEventDay = 0;
    if (basicEvent?.eventStartDateTime) {
      const evStart = parseInt(String(basicEvent.eventStartDateTime), 10);
      const evEnd = basicEvent.eventEndDateTime
        ? parseInt(String(basicEvent.eventEndDateTime), 10)
        : null;

      usedOnEventDay = participants.filter((t) => {
        const isUsed = t.isUsed === "1" || t.isUsed === true || (t.usedAt && t.usedAt > 0);
        if (!isUsed || !t.usedDateTime) return false;
        const used = parseInt(String(t.usedDateTime), 10);
        const sameDay = sameDayTR(used, evStart);
        if (evEnd) {
          const inRange = used >= evStart && used <= evEnd;
          return sameDay || inRange;
        }
        return sameDay;
      }).length;
    }

    // Total Used (all time)
    const totalUsed = participants.filter(
      (t) => t.isUsed === "1" || t.isUsed === true || (t.usedAt && t.usedAt > 0)
    ).length;

    // Participation Rate
    const participationRate =
      totalSold > 0 ? Math.round((totalUsed / totalSold) * 100) : 0;

    // Daily Sales
    const today = new Date();
    const todayKey = today.toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toLocaleDateString("tr-TR", {
      timeZone: "Europe/Istanbul",
    });

    const soldToday = participants.filter((t) => {
      if (!t.paymentDateTime && !t.createdAt) return false;
      const paymentDate = t.paymentDateTime || t.createdAt || 0;
      const key = new Date(paymentDate * 1000).toLocaleDateString("tr-TR", {
        timeZone: "Europe/Istanbul",
      });
      return key === todayKey;
    }).length;

    const soldYesterday = participants.filter((t) => {
      if (!t.paymentDateTime && !t.createdAt) return false;
      const paymentDate = t.paymentDateTime || t.createdAt || 0;
      const key = new Date(paymentDate * 1000).toLocaleDateString("tr-TR", {
        timeZone: "Europe/Istanbul",
      });
      return key === yesterdayKey;
    }).length;

    const dailySalesChange =
      soldYesterday > 0
        ? Math.round(((soldToday - soldYesterday) / soldYesterday) * 100)
        : soldToday > 0
        ? 100
        : 0;

    // Cancelled
    const cancelledCount = participants.filter((t) => t.status === "0").length;

    return {
      totalSold,
      totalRevenue,
      refundedCount,
      refundedRevenue,
      netBalance,
      usedOnEventDay,
      totalUsed,
      participationRate,
      soldToday,
      soldYesterday,
      dailySalesChange,
      cancelledCount,
    };
  }, [participants, basicEvent?.eventStartDateTime, basicEvent?.eventEndDateTime]);

  // Format today's date for display
  const formatTodayDate = (): string => {
    const today = new Date();
    return today.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "Europe/Istanbul",
    });
  };

  // Available stats options
  const statsOptions = [
    { value: 'totalSold', label: getTranslation("organizerPanel.totalSoldTickets") || "Total Sold Tickets" },
    { value: 'totalRevenue', label: getTranslation("organizerPanel.totalRevenue") || "Total Revenue" },
    { value: 'participationRate', label: getTranslation("organizerPanel.participationRate") || "Participation Rate" },
    { value: 'dailySales', label: getTranslation("organizerPanel.dailySales") || "Daily Sales" },
    { value: 'refunds', label: getTranslation("organizerPanel.refunds") || "Refunds" },
    { value: 'balance', label: getTranslation("organizerPanel.balance") || "Balance" },
    { value: 'usedOnEventDay', label: getTranslation("organizerPanel.usedOnEventDay") || "Used on Event Day" },
    { value: 'cancelled', label: getTranslation("organizerPanel.cancelled") || "Cancelled" },
  ];

  // Handle stat toggle
  const handleStatToggle = (statValue: string) => {
    setSelectedStats(prev => {
      if (prev.includes(statValue)) {
        return prev.filter(s => s !== statValue);
      } else {
        return [...prev, statValue];
      }
    });
  };

  // Filter and sort participants
  const filteredAndSortedParticipants = useMemo(() => {
    let result = [...participants];

    // Search filter - search across name, email, phone, ticketNo, ticketType
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((participant) => {
        const fullName = `${participant.name} ${participant.lastname}`.toLowerCase();
        const email = (participant.email || '').toLowerCase();
        const phone = (participant.phone || '').toLowerCase();
        const ticketNo = (participant.ticketNo || '').toLowerCase();
        const ticketType = (participant.title || participant.type || '').toLowerCase();
        
        return fullName.includes(query) || 
               email.includes(query) || 
               phone.includes(query) || 
               ticketNo.includes(query) ||
               ticketType.includes(query);
      });
    }

    // Sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (sortColumn) {
          case 'participantName':
            aValue = `${a.name} ${a.lastname}`.toLowerCase();
            bValue = `${b.name} ${b.lastname}`.toLowerCase();
            break;
          case 'details':
            aValue = (a.email || '').toLowerCase();
            bValue = (b.email || '').toLowerCase();
            break;
          case 'ticketType':
            aValue = (a.title || a.type || '').toLowerCase();
            bValue = (b.title || b.type || '').toLowerCase();
            break;
          case 'checkIn':
            // Sort by check-in status: used (true/"1") > not used (false/"0")
            const aIsUsed = a.isUsed === true || a.isUsed === "1";
            const bIsUsed = b.isUsed === true || b.isUsed === "1";
            aValue = aIsUsed ? 1 : 0;
            bValue = bIsUsed ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Reverse the order (newest first)
    return result.reverse();
  }, [participants, searchQuery, sortColumn, sortDirection]);

  return (
    <div className={styles.eventParticipantsPage}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>
          {getTranslation('organizerPanel.eventParticipants') || 'Etkinlik Katılımcıları'}
        </h2>
        <div className={styles.headerActions}>
          <div className={styles.searchContainer}>
            <FormInput
              type="text"
              placeholder={getTranslation('table.searchParticipants') || 'Search participants...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              disabled={loading}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowStatsModal(true)}
            className={styles.iconButton}
            title={getTranslation('organizerPanel.selectStats') || 'Stats'}
            aria-label={getTranslation('organizerPanel.selectStats') || 'Stats'}
          >
            {/* @ts-ignore */}
            <HiChartBar size={20} />
            <span className={styles.iconButtonText}>
              {getTranslation('organizerPanel.selectStats') || 'Stats'}
            </span>
          </button>
          <button
            type="button"
            onClick={handleExportToExcel}
            disabled={exporting || loading || filteredAndSortedParticipants.length === 0}
            className={styles.iconButton}
            title={getTranslation('organizerPanel.export') || 'Export'}
            aria-label={getTranslation('organizerPanel.export') || 'Export'}
          >
            {/* @ts-ignore */}
            <HiDownload size={20} />
            <span className={styles.iconButtonText}>
              {exporting
                ? (getTranslation('organizerPanel.exporting') || 'Exporting...')
                : (getTranslation('organizerPanel.export') || 'Export')
              }
            </span>
          </button>
          <Button
            variant="primary"
            size="medium"
            onClick={() => setShowBulkImportModal(true)}
            className={styles.importButton}
          >
            {/* @ts-ignore */}
            <HiPlus size={18} />
            {getTranslation('organizerPanel.addParticipant') || 'Katılımcı Ekle'}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={showCheckInModal}
        onClose={() => {
          setShowCheckInModal(false);
          setSelectedParticipant(null);
        }}
        title={getTranslation('organizerPanel.confirmCheckIn') || 'Check-in Onayı'}
        variant="confirm"
        message={getTranslation('organizerPanel.checkInMessage') || 'Bu katılımcıyı check-in yapmak istediğinizden emin misiniz?'}
        confirmText={getTranslation('common.confirm') || 'Onayla'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleConfirmCheckIn}
        confirmVariant="primary"
        isDanger={false}
      />

      {/* Stats Selection Modal */}
      <Modal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        title={getTranslation('organizerPanel.selectStatsToShow') || 'Select Stats to Show'}
        variant="default"
      >
        <div className={styles.statsModalContent}>
          {statsOptions.map((option) => (
            <div key={option.value} className={styles.statOption}>
              <FormToggle
                checked={selectedStats.includes(option.value)}
                onChange={() => handleStatToggle(option.value)}
                label={option.label}
              />
            </div>
          ))}
          <div className={styles.statsModalActions}>
            <Button
              variant="primary"
              size="medium"
              onClick={() => setShowStatsModal(false)}
            >
              {getTranslation('common.save') || 'Save'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Metric Cards - Ordered to match layout: Row 1: Total Sold, Total Revenue, Participation Rate, Daily Sales; Row 2: Refunds, Balance, Used on Event Day, Cancelled */}
      <div className={styles.metricsGrid}>
        {/* Row 1 - Card 1: Total Sold Tickets */}
        {selectedStats.includes('totalSold') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiCollection size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.totalSoldTickets") || "Total Sold Tickets"}
              </div>
              <div className={styles.metricValue}>{metrics.totalSold}</div>
              {metrics.cancelledCount > 0 && (
                <div className={styles.metricSubLabel}>
                  {metrics.cancelledCount} {getTranslation("organizerPanel.cancelled") || "cancelled"} out of {participants.length} total
                </div>
              )}
            </div>
          </div>
        )}

        {/* Row 1 - Card 2: Total Revenue */}
        {selectedStats.includes('totalRevenue') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiCash size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.totalRevenue") || "Total Revenue"}
              </div>
              <div className={styles.metricValue}>{formatCurrency(metrics.totalRevenue)}</div>
              <div className={styles.metricSubLabel}>
                {getTranslation("organizerPanel.grossExcludingRefunds") || "Gross (excluding refunds)"}
              </div>
            </div>
          </div>
        )}

        {/* Row 1 - Card 3: Participation Rate */}
        {selectedStats.includes('participationRate') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiChartBar size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.participationRate") || "Participation Rate"}
              </div>
              <div className={styles.metricValue}>{metrics.participationRate}%</div>
            </div>
          </div>
        )}

        {/* Row 1 - Card 4: Daily Sales */}
        {selectedStats.includes('dailySales') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiTrendingUp size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.dailySales") || "Daily Sales"}
              </div>
              <div className={styles.metricValue}>{metrics.soldToday}</div>
              <div className={styles.metricSubLabel}>
                {formatTodayDate()}
                {metrics.dailySalesChange !== 0 && (
                  <span
                    className={`${styles.dailyChange} ${
                      metrics.dailySalesChange > 0
                        ? styles.dailyChangePositive
                        : styles.dailyChangeNegative
                    }`}
                  >
                    {" "}
                    {metrics.dailySalesChange > 0 ? "↑" : "↓"} {Math.abs(metrics.dailySalesChange)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Row 2 - Card 1: Refunds */}
        {selectedStats.includes('refunds') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiArrowLeft size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.refunds") || "Refunds"}
                {metrics.refundedCount > 0 && ` (${metrics.refundedCount})`}
              </div>
              <div className={styles.metricValue}>{formatCurrency(metrics.refundedRevenue)}</div>
            </div>
          </div>
        )}

        {/* Row 2 - Card 2: Net Balance */}
        {selectedStats.includes('balance') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiCreditCard size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.balance") || "Balance"}
              </div>
              <div className={styles.metricValue}>{formatCurrency(metrics.netBalance)}</div>
              <div className={styles.metricSubLabel}>
                {getTranslation("organizerPanel.afterRefunds") || "After refunds"}
              </div>
            </div>
          </div>
        )}

        {/* Row 2 - Card 3: Used on Event Day */}
        {selectedStats.includes('usedOnEventDay') && (
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiCalendar size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.usedOnEventDay") || "Used on Event Day"}
              </div>
              <div className={styles.metricValue}>{metrics.usedOnEventDay}</div>
            </div>
          </div>
        )}

        {/* Row 2 - Card 4: Cancelled */}
        {selectedStats.includes('cancelled') && (
          <div className={`${styles.metricCard} ${styles.metricCardDanger}`}>
            <div className={styles.metricIcon}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiX size={20} />
            </div>
            <div className={styles.metricContent}>
              <div className={styles.metricLabel}>
                {getTranslation("organizerPanel.cancelled") || "Cancelled"}
              </div>
              <div className={styles.metricValue}>{metrics.cancelledCount}</div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile search row - appears after metrics on mobile */}
      <div className={styles.mobileSearchRow}>
        <div className={styles.mobileSearchContainer}>
          <FormInput
            type="text"
            placeholder={getTranslation('table.searchParticipants') || 'Search participants...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.mobileSearchInput}
            disabled={loading}
          />
        </div>
        <button
          type="button"
          onClick={() => setShowBulkImportModal(true)}
          className={styles.mobileIconButton}
          title={getTranslation('organizerPanel.addParticipant') || 'Katılımcı Ekle'}
          aria-label={getTranslation('organizerPanel.addParticipant') || 'Katılımcı Ekle'}
        >
          {/* @ts-ignore */}
          <HiPlus size={20} />
        </button>
        <button
          type="button"
          onClick={() => setShowStatsModal(true)}
          className={styles.mobileIconButton}
          title={getTranslation('organizerPanel.selectStats') || 'Stats'}
          aria-label={getTranslation('organizerPanel.selectStats') || 'Stats'}
        >
          {/* @ts-ignore */}
          <HiChartBar size={20} />
        </button>
        <button
          type="button"
          onClick={handleExportToExcel}
          disabled={exporting || loading || filteredAndSortedParticipants.length === 0}
          className={styles.mobileIconButton}
          title={getTranslation('organizerPanel.export') || 'Export'}
          aria-label={getTranslation('organizerPanel.export') || 'Export'}
        >
          {/* @ts-ignore */}
          <HiDownload size={20} />
        </button>
      </div>

      <Table>
        <div className={styles.tableHeaderWrapper}>
          <div className={styles.checkboxHeader}>
            <input
              type="checkbox"
              checked={selectedParticipants.size === filteredAndSortedParticipants.length && filteredAndSortedParticipants.length > 0}
              onChange={handleSelectAll}
              disabled={loading || filteredAndSortedParticipants.length === 0}
              className={styles.checkbox}
            />
          </div>
          <TableHeader
            columns={[
              getTranslation('organizerPanel.participantName') || 'Katılımcı',
              getTranslation('organizerPanel.details') || 'Detaylar',
              getTranslation('organizerPanel.tags') || 'Tags',
              getTranslation('organizerPanel.ticketType') || 'Bilet Tipi',
              getTranslation('organizerPanel.checkIn') || 'Check-in'
            ]}
            columnWidths={['2fr', '2fr', '1fr', '1.5fr', '80px']}
            columnKeys={['participantName', 'details', 'tags', 'ticketType', 'checkIn']}
            sortableColumns={['participantName', 'details', 'ticketType', 'checkIn']}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        </div>
        <TableBody>
          {loading ? (
            // Skeleton loading rows
            Array.from({ length: 5 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.tableRowWrapper}>
                <div className={styles.checkboxCell}>
                  <LoadingSkeleton height={18} width={18} />
                </div>
                <TableRow columnWidths={['2fr', '2fr', '1fr', '1.5fr', '80px']}>
                  <TableCell>
                    <div className={styles.participantInfo}>
                      <LoadingSkeleton height={20} width="60%" />
                      <div style={{ marginTop: '8px' }}>
                        <LoadingSkeleton height={14} width="40%" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={styles.participantDetails}>
                      <LoadingSkeleton height={16} width="80%" />
                      <div style={{ marginTop: '4px' }}>
                        <LoadingSkeleton height={14} width="60%" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <LoadingSkeleton height={20} width="60%" />
                  </TableCell>
                  <TableCell>
                    <LoadingSkeleton height={16} width="70%" />
                  </TableCell>
                  <TableCell>
                    <LoadingSkeleton height={32} width={32} variant="circular" />
                  </TableCell>
                </TableRow>
              </div>
            ))
          ) : filteredAndSortedParticipants.length === 0 ? (
            <EmptyTableRow message={getTranslation('organizerPanel.noParticipants') || 'Katılımcı bulunamadı'} />
          ) : (
            filteredAndSortedParticipants.map((participant) => {
              const status = getParticipantStatus(participant);
              const fullName = `${participant.name} ${participant.lastname}`;
              const ticketType = participant.title || participant.type || getTranslation('organizerPanel.unknownTicket') || 'Bilinmeyen';
              const participantTags = getParticipantTags(participant);
              const isSelected = selectedParticipants.has(participant.ticketNo);

              const isUsed = status === 'used';
              const isDisabled = status === 'cancelled' || status === 'refunded';
              const isLoading = checkingIn === participant.ticketNo;

              return (
                <div key={participant.ticketNo} className={`${styles.tableRowWrapper} ${isSelected ? styles.tableRowSelected : ''}`}>
                  <div className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectParticipant(participant.ticketNo)}
                      className={styles.checkbox}
                    />
                  </div>
                  <TableRow
                    columnWidths={['2fr', '2fr', '1fr', '1.5fr', '80px']}
                  >
                    <TableCell>
                      <div className={styles.participantInfo}>
                        <UserProfileLink
                          userId={(participant as any).userID || (participant as any).id}
                          name={participant.name}
                          lastname={participant.lastname}
                        >
                          <div className={styles.participantName}>{fullName}</div>
                        </UserProfileLink>
                        {participant.ticketNo && (
                          <div className={styles.ticketNo}>
                            {getTranslation('organizerPanel.ticketNo') || 'Bilet No'}: {participant.ticketNo}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.participantDetails}>
                        <div className={styles.participantEmail}>{participant.email}</div>
                        {participant.phone && (
                          <div className={styles.participantPhone}>{participant.phone}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        ref={activeTagDropdown === participant.ticketNo ? tagDropdownAnchorRef : undefined}
                      >
                        <UserTagsDisplay
                          tags={participantTags}
                          onClick={() => setActiveTagDropdown(activeTagDropdown === participant.ticketNo ? null : participant.ticketNo)}
                          emptyText={getTranslation('organizerPanel.noTags') || 'No tags'}
                        />
                      </span>
                      {activeTagDropdown === participant.ticketNo && (
                        <TagDropdown
                          isOpen={true}
                          onClose={() => setActiveTagDropdown(null)}
                          availableTags={segments}
                          assignedTags={participantTags}
                          onTagToggle={(tag, isAssigned) => handleTagToggle(tag, isAssigned, participant)}
                          onCreateTag={(tag) => handleCreateAndAssignTag(tag, participant)}
                          anchorRef={tagDropdownAnchorRef}
                          emptyMessage={getTranslation('organizerPanel.noSegments') || 'No segments'}
                          createPlaceholder={getTranslation('organizerPanel.createNewTag') || 'Create new tag...'}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className={styles.ticketTypeContainer}>
                        <div className={styles.ticketType}>{ticketType}</div>
                        <div className={styles.ticketPrice}>{getTicketPrice(participant)}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <button
                        className={`${styles.checkInToggle} ${isUsed ? styles.checkInToggleChecked : ''} ${isDisabled ? styles.checkInToggleDisabled : ''}`}
                        onClick={() => !isDisabled && !isUsed && handleCheckInToggle(participant)}
                        disabled={isDisabled || isUsed || isLoading}
                        aria-label={isUsed ? (getTranslation('organizerPanel.statusUsed') || 'Kullanıldı') : (getTranslation('organizerPanel.checkIn') || 'Check-in')}
                      >
                        {isLoading ? (
                          <div className={styles.toggleSpinner}></div>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </TableCell>
                  </TableRow>
                </div>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        eventID={basicEvent?.eventID || ''}
        channelID={channel?.channelID || ''}
        tickets={eventDetails?.tickets || []}
        existingEmails={participants.map((p) => p.email || '').filter(Boolean)}
        token={token || ''}
        onImportSuccess={fetchParticipants}
        getTranslation={getTranslation}
        toastSuccess={toastSuccess}
        toastError={toastError}
        createSoldTicket={Services.SocialHiveCreateSoldTicket}
      />

      {/* Bulk Tag Modal */}
      <Modal
        isOpen={showBulkTagModal}
        onClose={handleCancelBulkTags}
        title={getTranslation('organizerPanel.manageTags') || 'Manage Tags'}
        variant="default"
      >
        <div className={styles.bulkTagModalContent}>
          {segments.length === 0 ? (
            <div className={styles.bulkTagEmptyMessage}>
              {getTranslation('organizerPanel.noSegments') || 'No segments created yet'}
            </div>
          ) : (
            <>
              <div className={styles.bulkTagList}>
                {segments.map((tag) => {
                  const pending = pendingBulkTags[tag];
                  const selectedUsers = filteredAndSortedParticipants.filter(p => selectedParticipants.has(p.ticketNo));
                  const usersWithTag = selectedUsers.filter(p => getParticipantTags(p).includes(tag)).length;
                  const usersWithoutTag = selectedUsers.length - usersWithTag;
                  return (
                    <div key={tag}>
                      <div className={styles.bulkTagItem}>
                        <div className={styles.bulkTagItemLabel}>
                          <UserTagsDisplay tags={[tag]} maxVisible={1} />
                        </div>
                        <div className={styles.bulkTagItemActions}>
                          <button
                            type="button"
                            className={`${styles.bulkTagActionBtn} ${pending === 'remove' ? styles.removeActive : ''}`}
                            onClick={() => handleBulkRemoveTag(tag)}
                            title={getTranslation('organizerPanel.removeTagFromUsers') || 'Remove from selected users'}
                          >
                            {pending === 'remove' ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            className={`${styles.bulkTagActionBtn} ${pending === 'add' ? styles.addActive : ''}`}
                            onClick={() => handleBulkAddTag(tag)}
                            title={getTranslation('organizerPanel.addTagToUsers') || 'Add to selected users'}
                          >
                            {pending === 'add' ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      {pending === 'add' && (() => {
                        const parts = (getTranslation('organizerPanel.tagWillBeAdded') || 'Will be added to {count} users who don\'t have it').split('{count}');
                        return (
                          <div className={`${styles.bulkTagWarning} ${styles.bulkTagWarningAdd}`}>
                            {parts[0]}<strong>{usersWithoutTag}{parts[1]}</strong>
                          </div>
                        );
                      })()}
                      {pending === 'remove' && (() => {
                        const parts = (getTranslation('organizerPanel.tagWillBeRemoved') || 'Will be removed from {count} users who have it').split('{count}');
                        return (
                          <div className={`${styles.bulkTagWarning} ${styles.bulkTagWarningRemove}`}>
                            {parts[0]}<strong>{usersWithTag}{parts[1]}</strong>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
              <div className={styles.bulkTagActions}>
                <Button variant="secondary" size="small" onClick={handleCancelBulkTags}>
                  {getTranslation('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSaveBulkTags}
                  disabled={Object.keys(pendingBulkTags).length === 0}
                >
                  {getTranslation('common.save') || 'Save'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Floating Bulk Selection Action Bar */}
      {selectedParticipants.size > 0 && (
        <div className={styles.bulkActionBar}>
          <span className={styles.bulkActionText}>
            {(getTranslation('organizerPanel.selectedUsers') || '{count} selected').replace('{count}', String(selectedParticipants.size))}
          </span>
          <Button
            variant="ghost"
            size="small"
            className={styles.clearButton}
            onClick={() => setSelectedParticipants(new Set())}
          >
            {getTranslation('common.clearSelection') || 'Clear'}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => { setPendingBulkTags({}); setShowBulkTagModal(true); }}
          >
            {/* @ts-ignore */}
            <HiTag size={16} />
            {getTranslation('organizerPanel.manageTags') || 'Manage Tags'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EventParticipantsPage;
